import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import {
  ContactChannel,
  type ReceivedAtCondition as ReceivedAtConditionType,
} from "@redotech/redo-flows-common/rules-v3/schemas";
import { RuleTriggerType } from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import {
  RedoSingleSelectDropdownInput,
  RedoSingleSelectDropdownInputButton,
} from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useState } from "react";
import { EmailIntegrationsContext } from "../../../../services/email-integrations";
import { commitPendingTagInput } from "../../commit-pending-tag-input";

const channelOptions = () => [
  {
    id: ContactChannel.EMAIL,
    type: "text" as const,
    text: t("Email"),
    value: ContactChannel.EMAIL,
  },
];

const ReceivedAtIntegratedEmail = memo(function ReceivedAtIntegratedEmail({
  condition,
  onChange,
  editableFields,
}: {
  condition: ReceivedAtConditionType;
  onChange: (updated: ReceivedAtConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const channelEditable = isEditable("channel");
  const valuesEditable = isEditable("values");

  const [availableEmails, setAvailableEmails] = useState<string[]>([]);
  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);
  const team = useRequiredContext(TeamContext);

  const selectedChannel = useMemo(
    () =>
      channelOptions().find(
        (opt) => opt.value === (condition.channel ?? ContactChannel.EMAIL),
      ),
    [condition.channel],
  );

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }

    const emails = [
      ...(emailIntegrationsLoad.value.customIntegrations || []),
      ...(emailIntegrationsLoad.value.gmailIntegrations || []),
      ...(emailIntegrationsLoad.value.outlookIntegrations || []),
    ].map((integration) => integration.email);

    const defaultEmail = team.settings?.support?.defaultEmail;
    if (defaultEmail && !emails.includes(defaultEmail)) {
      emails.push(defaultEmail);
    }

    setAvailableEmails((oldEmails) => {
      const updatedEmails = [...oldEmails];
      for (const email of emails) {
        if (!updatedEmails.includes(email)) {
          updatedEmails.push(email);
        }
      }
      return updatedEmails;
    });
  }, [emailIntegrationsLoad.value, team]);

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const emailItems: RedoListItem<string>[] = useMemo(() => {
    const emails = availableEmails.length
      ? availableEmails
      : [t("No email addresses configured")];
    return emails.map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [availableEmails, t]);

  const selectedItems: RedoListItem<string>[] = useMemo(() => {
    return (condition.values || []).map((v) => ({
      id: v,
      type: "text" as const,
      text: v,
      value: v,
    }));
  }, [condition.values]);

  const displayItem: RedoListItem<string> | undefined = useMemo(() => {
    if (selectedItems.length === 0) {
      return undefined;
    }
    if (selectedItems.length === 1) {
      return selectedItems[0];
    }
    return {
      id: "display",
      type: "text" as const,
      text: selectedItems
        .flatMap((item) => ("text" in item ? [item.text] : []))
        .join(", "),
      value: "",
    };
  }, [selectedItems]);

  const isDisabled = availableEmails.length === 0 || !valuesEditable;

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        disabled={!channelEditable}
        options={channelOptions()}
        optionSelected={({ item }) => {
          if (!channelEditable) {
            return;
          }
          onChange({ ...condition, channel: item.value, values: [] });
        }}
        placeholder={t("Select channel")}
        selectedItem={selectedChannel}
      />
      {(condition.channel ?? ContactChannel.EMAIL) === ContactChannel.EMAIL && (
        <Flex dir="column" gap="sm" w="full">
          <RedoSingleSelectDropdownInputButton
            disabled={isDisabled}
            isDropdownOpen={dropdownOpen}
            onClick={() => {
              if (!isDisabled) {
                setDropdownOpen(!dropdownOpen);
              }
            }}
            placeholder={t("Select email addresses")}
            ref={setDropdownButtonRef}
            selectedItem={displayItem}
            size="xs"
          />
          <RedoMultiselectDropdown
            dropdownAnchor={dropdownButtonRef}
            dropdownOpen={dropdownOpen}
            fitToAnchor
            items={emailItems}
            selectedItems={selectedItems}
            selectionVariant="checkbox"
            setDropdownOpen={setDropdownOpen}
            setSelectedItems={(items) => {
              if (!valuesEditable) {
                return;
              }
              onChange({
                ...condition,
                values: items.map((item) => item.value),
              });
            }}
            size="xs"
          />
        </Flex>
      )}
    </Flex>
  );
});

const ReceivedAtFreeTextEmail = memo(function ReceivedAtFreeTextEmail({
  condition,
  onChange,
  editableFields,
}: {
  condition: ReceivedAtConditionType;
  onChange: (updated: ReceivedAtConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const channelEditable = isEditable("channel");
  const valuesEditable = isEditable("values");
  const [inputValue, setInputValue] = useState("");

  const selectedChannel = useMemo(
    () =>
      channelOptions().find(
        (opt) => opt.value === (condition.channel ?? ContactChannel.EMAIL),
      ),
    [condition.channel],
  );

  const commitPendingInput = () => {
    if (!valuesEditable) {
      return;
    }
    const values = commitPendingTagInput(condition.values || [], inputValue);
    if (!values) {
      return;
    }
    onChange({ ...condition, values });
    setInputValue("");
  };

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        disabled={!channelEditable}
        options={channelOptions()}
        optionSelected={({ item }) => {
          if (!channelEditable) {
            return;
          }
          onChange({ ...condition, channel: item.value, values: [] });
        }}
        placeholder={t("Select channel")}
        selectedItem={selectedChannel}
      />
      {(condition.channel ?? ContactChannel.EMAIL) === ContactChannel.EMAIL && (
        <Flex
          style={
            valuesEditable ? undefined : { pointerEvents: "none", opacity: 0.6 }
          }
        >
          <RedoTagInputField
            inputValue={inputValue}
            label={t("Recipient email address(es)")}
            onBlur={commitPendingInput}
            placeholder={t("Enter email addresses")}
            setInputValue={setInputValue}
            setTags={(emails) => {
              if (!valuesEditable) {
                return;
              }
              const unique = Array.from(new Set(emails));
              onChange({ ...condition, values: unique });
            }}
            state="default"
            tags={(condition.values || []).map((v) => ({ text: v }))}
          />
        </Flex>
      )}
    </Flex>
  );
});

export const ReceivedAtCondition = memo(function ReceivedAtCondition({
  condition,
  onChange,
  editableFields,
  triggerType,
}: {
  condition: ReceivedAtConditionType;
  onChange: (updated: ReceivedAtConditionType) => void;
  editableFields?: ReadonlySet<string>;
  triggerType?: RuleTriggerType;
}) {
  if (triggerType === RuleTriggerType.MESSAGE_RECEIVED) {
    return (
      <ReceivedAtIntegratedEmail
        condition={condition}
        editableFields={editableFields}
        onChange={onChange}
      />
    );
  }
  return (
    <ReceivedAtFreeTextEmail
      condition={condition}
      editableFields={editableFields}
      onChange={onChange}
    />
  );
});
